import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const TYPES = [
  { v: "image",   l: "Bilde" },
  { v: "video",   l: "Film" },
  { v: "novelle", l: "Novelle" },
  { v: "creator", l: "Skaper" },
];

// Forhåndsmaler – matcher Norsk Erotikks chip-økonomi (mai 2026)
const TEMPLATES = [
  {
    k: "weekly_image",   l: "Ukens bilde",      type: "image",
    prizes: [50, 25, 10],    pinDays: 7,    duration_days: 14,
    description: "Ukens bilde. Vinneren får førsteplassen pinnet på forsiden i 7 dager.",
    requires_approval: false, recurring: "weekly_image",
  },
  {
    k: "monthly_image",  l: "Månedens bilde",   type: "image",
    prizes: [100, 50, 25],   pinDays: 30,   duration_days: 35,
    description: "Månedens beste bilde – stemt fram av fellesskapet.",
    requires_approval: false, recurring: "monthly_image",
  },
  {
    k: "monthly_video",  l: "Månedens film",    type: "video",
    prizes: [100, 50, 25],   pinDays: 30,   duration_days: 35,
    description: "Månedens beste film. Krever VIP for visning, så større incentiv for skapere.",
    requires_approval: false, recurring: "monthly_video",
  },
  {
    k: "monthly_novelle",l: "Månedens novelle", type: "novelle",
    prizes: [200, 100, 50],  pinDays: 30,   duration_days: 35,
    description: "Månedens beste novelle.",
    requires_approval: false, recurring: "monthly_novelle",
  },
  {
    k: "yearly_image",   l: "Årets bilde",      type: "image",
    prizes: [400, 200, 100], pinDays: 365,  duration_days: 14,
    description: "Årets beste bilde – kåret blant alle månedens vinnere.",
    requires_approval: false, recurring: "yearly_image",
  },
  {
    k: "yearly_video",   l: "Årets film",       type: "video",
    prizes: [400, 200, 100], pinDays: 365,  duration_days: 14,
    description: "Årets beste film.",
    requires_approval: false, recurring: "yearly_video",
  },
  {
    k: "yearly_novelle", l: "Årets novelle",    type: "novelle",
    prizes: [400, 200, 100], pinDays: 365,  duration_days: 14,
    description: "Årets beste novelle.",
    requires_approval: false, recurring: "yearly_novelle",
  },
  {
    k: "seasonal_image", l: "Sesong – bilde",   type: "image",
    prizes: [100, 50, 25],   pinDays: 30,   duration_days: 21,
    description: "Sesongkonkurranse for bilde (Valentines / Sommer / Halloween / Jul).",
    requires_approval: false, recurring: "",
  },
  {
    k: "seasonal_video", l: "Sesong – film",    type: "video",
    prizes: [200, 100, 50],  pinDays: 30,   duration_days: 21,
    description: "Sesongkonkurranse for film.",
    requires_approval: false, recurring: "",
  },
  {
    k: "seasonal_novelle",l:"Sesong – novelle", type: "novelle",
    prizes: [200, 100, 50],  pinDays: 30,   duration_days: 21,
    description: "Sesongkonkurranse for novelle.",
    requires_approval: false, recurring: "",
  },
];

function toLocalISO(d) {
  // Konverter Date til "YYYY-MM-DDTHH:mm" (lokal tid for datetime-local input)
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminKonkurranseNy() {
  const nav = useNavigate();
  const { user, loading } = useAuth();

  // Standard: månedens bilde, neste mandag som start
  const today = new Date();
  const defaultStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0);
  const defaultVoting = new Date(defaultStart); defaultVoting.setDate(defaultVoting.getDate() + 14);
  const defaultEnd = new Date(defaultStart); defaultEnd.setDate(defaultEnd.getDate() + 35);

  const [type, setType] = useState("image");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [uploadAt, setUploadAt] = useState(toLocalISO(defaultStart));
  const [votingAt, setVotingAt] = useState(toLocalISO(defaultVoting));
  const [endsAt, setEndsAt] = useState(toLocalISO(defaultEnd));
  const [prizes, setPrizes] = useState([100, 50, 25]);
  const [recurring, setRecurring] = useState("");
  const [pinDays, setPinDays] = useState(30);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!loading && (!user || !["admin", "owner"].includes(user.role))) {
    return <Navigate to="/konkurranser" replace />;
  }

  const applyTemplate = (tpl) => {
    setType(tpl.type);
    setPrizes([...tpl.prizes]);
    setPinDays(tpl.pinDays);
    setDesc(tpl.description);
    setRequiresApproval(tpl.requires_approval);
    setRecurring(tpl.recurring);
    // Datoer: start nå, stemming etter 14 dager, slutt etter `duration_days`
    const start = new Date();
    const voting = new Date(); voting.setDate(voting.getDate() + Math.floor(tpl.duration_days * 0.4));
    const ends   = new Date(); ends.setDate(ends.getDate() + tpl.duration_days);
    setUploadAt(toLocalISO(start));
    setVotingAt(toLocalISO(voting));
    setEndsAt(toLocalISO(ends));
    if (!title) setTitle(tpl.l);
    toast.success(`Mal aktivert: ${tpl.l}`);
  };

  const updatePrize = (idx, val) => {
    setPrizes((p) => p.map((x, i) => (i === idx ? Math.max(0, Number(val) || 0) : x)));
  };
  const addPlace = () => prizes.length < 10 && setPrizes((p) => [...p, 0]);
  const removePlace = () => prizes.length > 1 && setPrizes((p) => p.slice(0, -1));
  const totalPrize = prizes.reduce((a, b) => a + b, 0);

  const submit = async () => {
    if (!title || !uploadAt || !votingAt || !endsAt) {
      toast.error("Fyll inn tittel og alle datoer");
      return;
    }
    if (prizes.length === 0 || prizes[0] <= 0) {
      toast.error("Sett minst én premie (førsteplass må være > 0)");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/contests", {
        type, title, description: desc, banner_url: bannerUrl || null,
        upload_open_at: new Date(uploadAt).toISOString(),
        voting_open_at: new Date(votingAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        prize_amount: Number(prizes[0]) || 0,  // bakoverkompatibel
        prizes: prizes.map((p) => Number(p) || 0),
        recurring_category: recurring || null,
        pinned_duration_days: Number(pinDays) || 30,
        requires_approval: requiresApproval,
      });
      toast.success(`Konkurranse opprettet – totalt ${totalPrize} chips i premier`);
      nav(`/konkurranser/${r.data.contest.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke opprette");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-2">Ny konkurranse</h1>
        <p className="text-[var(--cream-soft)] text-sm mb-8">
          Velg en mal under for å sette opp en standard-konkurranse på sekunder, eller fyll inn manuelt.
        </p>

        {/* Maler */}
        <div className="mb-8">
          <label className="font-meta block mb-3 text-[var(--cream-soft)]">FORHÅNDSMALER</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {TEMPLATES.map((t) => (
              <button
                key={t.k}
                onClick={() => applyTemplate(t)}
                data-testid={`template-${t.k}`}
                className="px-3 py-2 text-xs border border-white/10 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60 hover:text-[var(--magenta)] transition text-left"
              >
                <div className="font-meta">{t.l}</div>
                <div className="text-[10px] text-[var(--cream-faint)] mt-0.5">
                  {t.prizes.join("/")} chips
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="font-meta block mb-2 text-[var(--cream-soft)]">TYPE</label>
            <div className="grid grid-cols-4 gap-2">
              {TYPES.map((t) => (
                <button key={t.v} onClick={() => setType(t.v)}
                  data-testid={`type-${t.v}`}
                  className={`px-4 py-2 text-sm border transition ${type === t.v ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/10 text-[var(--cream)]"}`}>
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          <Field label="TITTEL" value={title} onChange={setTitle} testId="contest-title" />
          <Field label="BESKRIVELSE" value={desc} onChange={setDesc} testId="contest-desc" multiline />
          <Field label="BANNER URL (VALGFRITT)" value={bannerUrl} onChange={setBannerUrl} testId="contest-banner" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="OPPLASTING STARTER" type="datetime-local" value={uploadAt} onChange={setUploadAt} testId="contest-upload" />
            <Field label="STEMMING STARTER"   type="datetime-local" value={votingAt} onChange={setVotingAt} testId="contest-voting" />
            <Field label="SLUTTER"             type="datetime-local" value={endsAt}   onChange={setEndsAt}   testId="contest-ends" />
          </div>

          {/* Premier per plass */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-meta text-[var(--cream-soft)]">PREMIER PER PLASS (CHIPS)</label>
              <div className="flex gap-1">
                <button onClick={removePlace} className="px-2 py-0.5 text-xs border border-white/10 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60" data-testid="prize-remove">−</button>
                <button onClick={addPlace} className="px-2 py-0.5 text-xs border border-white/10 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60" data-testid="prize-add">+</button>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {prizes.map((p, i) => (
                <div key={i}>
                  <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-1">
                    {i === 0 ? "1. PLASS" : i === 1 ? "2. PLASS" : i === 2 ? "3. PLASS" : `${i + 1}. PLASS`}
                  </div>
                  <input
                    type="number" min="0" value={p}
                    onChange={(e) => updatePrize(i, e.target.value)}
                    data-testid={`prize-${i + 1}`}
                    className="w-full bg-[var(--noir-elev)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/50 outline-none"
                  />
                </div>
              ))}
            </div>
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-2">
              Totalt utbetalt: <span className="text-[var(--magenta)]">{totalPrize} chips</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="PINNED DAGER (VINNER)" type="number" value={pinDays} onChange={setPinDays} testId="contest-pin" />
            <Field label="GJENTAKENDE KATEGORI" value={recurring} onChange={setRecurring} testId="contest-recurring" placeholder="f.eks monthly_image" />
          </div>

          <label className="flex items-center gap-3 cursor-pointer p-3 border border-white/10 hover:border-[var(--magenta)]/40 transition" data-testid="contest-requires-approval-label">
            <input
              type="checkbox"
              checked={requiresApproval}
              onChange={(e) => setRequiresApproval(e.target.checked)}
              className="accent-[var(--magenta)] h-4 w-4"
              data-testid="contest-requires-approval"
            />
            <div>
              <div className="font-fraunces italic text-[var(--cream)]">Krev godkjenning av bidrag</div>
              <div className="font-manrope text-xs text-[var(--cream-soft)] mt-0.5">
                Hvis aktivert, må du godkjenne hvert bidrag før det vises offentlig og kan stemmes på.
                Anbefales for sensitive konkurranser (film, sesongtemaer).
              </div>
            </div>
          </label>

          <button onClick={submit} disabled={saving} data-testid="contest-save"
            className="w-full px-6 py-3 bg-[var(--magenta)] text-black font-meta disabled:opacity-50">
            {saving ? "OPPRETTER…" : `OPPRETT – TOTALT ${totalPrize} CHIPS`}
          </button>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, value, onChange, type = "text", multiline = false, testId, placeholder }) {
  const cls = "w-full bg-[var(--noir-elev)] border border-white/10 px-4 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/50 outline-none";
  return (
    <div>
      <label className="font-meta block mb-2 text-[var(--cream-soft)]">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className={cls} data-testid={testId} placeholder={placeholder} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId} placeholder={placeholder} />
      )}
    </div>
  );
}
